import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import DatePicker from "../components/DatePicker";
import { Plus, ArrowRight, Edit, Trash2 } from "lucide-react";
import { api, toast } from "../App";
import { RfqContactSelect } from "../components/rfq/RfqContactSelect";

export default function RFQs({ user }) {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("initiated");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_contact_id: "", project_name: "", received_date: "", due_date: "", high_priority: false, notes: "" });
  const [clientSearch, setClientSearch] = useState("");
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelDialog, setCancelDialog] = useState({ open: false, rfq: null, remarks: "", saving: false });

  const canManage = ["Sales", "Management"].includes(user?.department) || user?.access_level === "Admin";
  const canConvert = user?.department === "Management" || user?.access_level === "Admin";

  const load = async () => {
    setLoading(true);
    try {
      const [rfqRes, clientsRes] = await Promise.all([
        api.get(`/rfqs?status=${statusFilter}`),
        api.get("/clients"),
      ]);
      setRfqs(rfqRes.data || []);
      setClients(clientsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load RFQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const filteredClients = clientSearch
    ? clients.filter((c) => (`${c.client_number || ""} ${c.client_name || c.name || ""}`).toLowerCase().includes(clientSearch.toLowerCase()))
    : clients;

  const filteredRfqs = search
    ? rfqs.filter((r) =>
        (r.rfq_number || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.client_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.project_name || "").toLowerCase().includes(search.toLowerCase()))
    : rfqs;

  const openNew = () => {
    setEditingId(null);
    setForm({ client_id: "", client_contact_id: "", project_name: "", received_date: "", due_date: "", high_priority: false, notes: "" });
    setClientSearch("");
    setDialogOpen(true);
  };

  const openEdit = (rfq) => {
    setEditingId(rfq.id);
    setForm({
      client_id: rfq.client_id,
      client_contact_id: rfq.client_contact_id || "",
      project_name: rfq.project_name || "",
      received_date: rfq.received_date || "",
      due_date: rfq.due_date || "",
      high_priority: !!rfq.high_priority,
      notes: rfq.notes || "",
    });
    setClientSearch(`${clients.find((c) => c.id === rfq.client_id)?.client_number || ""} - ${rfq.client_name || ""}`);
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.client_id) { toast.error("Please select a client"); return; }
    if (!form.client_contact_id) { toast.error("Please select the client contact (Estimator)"); return; }
    if (!form.project_name?.trim()) { toast.error("Project name is required"); return; }
    if (!form.received_date) { toast.error("Received date is required"); return; }
    try {
      setSaving(true);
      const payload = {
        client_id: form.client_id,
        client_contact_id: form.client_contact_id,
        project_name: form.project_name.trim(),
        received_date: form.received_date,
        due_date: form.due_date || null,
        high_priority: !!form.high_priority,
        notes: (form.notes || "").trim() || null,
      };
      if (editingId) {
        await api.put(`/rfqs/${editingId}`, payload);
        toast.success("RFQ updated");
      } else {
        const res = await api.post("/rfqs", payload);
        toast.success(`RFQ ${res.data.rfq_number} created`);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save RFQ");
    } finally {
      setSaving(false);
    }
  };

  const openCancel = (rfq) => {
    setCancelDialog({ open: true, rfq, remarks: "", saving: false });
  };

  const confirmCancel = async () => {
    const remarks = (cancelDialog.remarks || "").trim();
    if (remarks.length < 3) {
      toast.error("Please add a short reason (at least 3 characters)");
      return;
    }
    try {
      setCancelDialog((d) => ({ ...d, saving: true }));
      await api.delete(`/rfqs/${cancelDialog.rfq.id}`, { data: { cancellation_remarks: remarks } });
      toast.success("RFQ cancelled");
      setCancelDialog({ open: false, rfq: null, remarks: "", saving: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel RFQ");
      setCancelDialog((d) => ({ ...d, saving: false }));
    }
  };

  return (
    <Layout user={user}>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-md3-headline-large text-md3-on-surface">RFQs</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant mt-1">
              Requests For Quote — pre-estimation records. Convert to Estimation when ready.
            </p>
          </div>
          {canManage && (
            <Button
              onClick={openNew}
              data-testid="rfqs-new-btn"
              className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 rounded-md3-full"
            >
              <Plus className="h-4 w-4 mr-1" /> New RFQ
            </Button>
          )}
        </div>

        <Card className="p-6 border-0 shadow-md3-2 rounded-md3-lg">
          <div className="flex items-end gap-3 mb-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs text-gray-600">Search</Label>
              <Input
                placeholder="Search by number, client or project..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="rfqs-search"
              />
            </div>
            <div className="w-48">
              <Label className="text-xs text-gray-600">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="rfqs-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="initiated">Initiated (active)</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-md3-on-surface-variant">Loading...</div>
          ) : filteredRfqs.length === 0 ? (
            <div className="text-center py-8 text-md3-on-surface-variant">No RFQs found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>RFQ #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRfqs.map((rfq) => (
                  <TableRow
                    key={rfq.id}
                    className={rfq.high_priority ? "bg-red-50 border-l-4 border-l-red-500" : ""}
                    data-testid={`rfq-row-${rfq.id}`}
                  >
                    <TableCell className="font-medium text-indigo-700">{rfq.rfq_number}</TableCell>
                    <TableCell>{rfq.client_name}</TableCell>
                    <TableCell>{rfq.project_name}</TableCell>
                    <TableCell className="whitespace-nowrap" data-testid={`rfq-received-${rfq.id}`}>{rfq.received_date || "-"}</TableCell>
                    <TableCell className={rfq.high_priority ? "font-semibold text-red-700" : ""}>{rfq.due_date || "-"}</TableCell>
                    <TableCell>
                      {rfq.high_priority ? (
                        <Badge className="bg-red-600 text-white text-[10px] px-1.5 py-0.5">HIGH</Badge>
                      ) : (
                        <span className="text-gray-400 text-xs">Normal</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        rfq.status === "initiated" ? "bg-indigo-600 text-white" :
                        rfq.status === "converted" ? "bg-green-600 text-white" :
                        "bg-gray-500 text-white"
                      }>
                        {rfq.status}
                      </Badge>
                      {rfq.status === "cancelled" && rfq.cancellation_remarks && (
                        <div
                          className="mt-1 text-[11px] text-gray-700 bg-gray-100 border-l-2 border-gray-400 pl-2 pr-1 py-1 rounded-sm max-w-xs whitespace-pre-wrap"
                          data-testid={`rfq-cancel-remarks-${rfq.id}`}
                          title={`Cancelled by ${rfq.cancelled_by_name || "—"}${rfq.cancelled_at ? ` on ${new Date(rfq.cancelled_at).toISOString().slice(0, 10)}` : ""}`}
                        >
                          <span className="font-medium text-gray-800">Reason:</span> {rfq.cancellation_remarks}
                          {rfq.cancelled_by_name && (
                            <div className="text-[10px] text-gray-500 mt-0.5">
                              — {rfq.cancelled_by_name}
                              {rfq.cancelled_at ? ` · ${new Date(rfq.cancelled_at).toISOString().slice(0, 10)}` : ""}
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-600">
                      {rfq.created_at ? new Date(rfq.created_at).toISOString().slice(0, 10) : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {rfq.status === "initiated" && (
                        <div className="flex justify-end gap-2">
                          {canConvert && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-md3-full h-8"
                              data-testid={`rfq-convert-${rfq.id}`}
                              onClick={() => navigate(`/estimations?from_rfq=${rfq.id}`)}
                              title="Convert to Estimation"
                            >
                              Convert <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          )}
                          {canManage && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="rounded-md3-full h-8"
                                data-testid={`rfq-edit-${rfq.id}`}
                                onClick={() => openEdit(rfq)}
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="rounded-md3-full h-8 text-red-700 hover:bg-red-50"
                                data-testid={`rfq-cancel-${rfq.id}`}
                                onClick={() => openCancel(rfq)}
                                title="Cancel RFQ"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                      {rfq.status === "converted" && rfq.converted_to_estimation_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-md3-full h-8 text-xs"
                          data-testid={`rfq-view-est-${rfq.id}`}
                          onClick={() => navigate(`/estimations`)}
                        >
                          View Estimation
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Create / Edit RFQ Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg bg-white" data-testid="rfqs-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit RFQ" : "New RFQ"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-md3-label-large text-gray-700 font-medium">Client *</Label>
                <div className="relative">
                  <Input
                    placeholder="Type to search by number or name..."
                    value={
                      form.client_id && !clientPopoverOpen
                        ? (() => {
                            const c = clients.find((x) => x.id === form.client_id);
                            return c ? `${c.client_number || ""} - ${c.client_name || c.name || ""}` : clientSearch;
                          })()
                        : clientSearch
                    }
                    onChange={(e) => { setClientSearch(e.target.value); setClientPopoverOpen(true); setForm({ ...form, client_id: "", client_contact_id: "" }); }}
                    onFocus={() => setClientPopoverOpen(true)}
                    data-testid="rfqs-client-search"
                  />
                  {clientPopoverOpen && clientSearch && filteredClients.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-auto bg-white border border-gray-200 rounded-md3-md shadow-md3-2">
                      {filteredClients.slice(0, 15).map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="block w-full text-left px-3 py-2 text-md3-body-small hover:bg-gray-100"
                          onClick={() => {
                            setForm({ ...form, client_id: c.id, client_contact_id: c.id === form.client_id ? form.client_contact_id : "" });
                            setClientSearch(`${c.client_number || ""} - ${c.client_name || c.name || ""}`);
                            setClientPopoverOpen(false);
                          }}
                        >
                          <span className="font-medium">{c.client_number || "-"}</span> · {c.client_name || c.name || "Unknown"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <RfqContactSelect clientId={form.client_id} value={form.client_contact_id} onChange={(v) => setForm((f) => ({ ...f, client_contact_id: v }))} />

              <div className="space-y-2">
                <Label htmlFor="dlg_project_name" className="text-md3-label-large text-gray-700 font-medium">Project Name *</Label>
                <Input
                  id="dlg_project_name"
                  value={form.project_name}
                  onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                  data-testid="rfqs-project-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dlg_received_date" className="text-md3-label-large text-gray-700 font-medium">Received Date *</Label>
                  <DatePicker
                    id="dlg_received_date"
                    data-testid="rfqs-received-date"
                    value={form.received_date}
                    onChange={(e) => setForm({ ...form, received_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dlg_due_date" className="text-md3-label-large text-gray-700 font-medium">Due Date</Label>
                  <DatePicker
                    id="dlg_due_date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  />
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="dlg_high_priority"
                      checked={!!form.high_priority}
                      onCheckedChange={(v) => setForm({ ...form, high_priority: !!v })}
                      data-testid="rfqs-high-priority"
                    />
                    <Label htmlFor="dlg_high_priority" className="text-md3-body-small text-red-700 font-medium cursor-pointer select-none">
                      High Priority
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dlg_notes" className="text-md3-label-large text-gray-700 font-medium">
                  Notes <span className="text-md3-body-small text-gray-500 font-normal">(any extra info received with this RFQ)</span>
                </Label>
                <Textarea
                  id="dlg_notes"
                  rows={4}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Scope details, attached drawings summary, client contact preferences, deadlines..."
                  data-testid="rfqs-notes"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-md3-full">Cancel</Button>
              <Button
                type="button"
                onClick={submit}
                disabled={saving}
                className="rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary"
                data-testid="rfqs-save-btn"
              >
                {saving ? "Saving..." : editingId ? "Update RFQ" : "Create RFQ"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel RFQ Dialog — requires audit remarks */}
        <Dialog
          open={cancelDialog.open}
          onOpenChange={(o) => !cancelDialog.saving && setCancelDialog((d) => ({ ...d, open: o }))}
        >
          <DialogContent className="max-w-md bg-white" data-testid="rfq-cancel-dialog">
            <DialogHeader>
              <DialogTitle>Cancel RFQ {cancelDialog.rfq?.rfq_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-md3-body-small text-md3-on-surface-variant">
                This will move the RFQ to <span className="font-medium text-gray-800">cancelled</span> and keep it visible in the audit trail.
                Please share a short reason — it will be visible to everyone.
              </p>
              <div className="space-y-2">
                <Label htmlFor="cancel_remarks" className="text-md3-label-large text-gray-700 font-medium">Reason for cancellation *</Label>
                <Textarea
                  id="cancel_remarks"
                  rows={4}
                  value={cancelDialog.remarks}
                  onChange={(e) => setCancelDialog((d) => ({ ...d, remarks: e.target.value }))}
                  placeholder="e.g. Client withdrew, out of scope, duplicate of RFQ 2026-3..."
                  data-testid="rfq-cancel-remarks-input"
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCancelDialog({ open: false, rfq: null, remarks: "", saving: false })}
                className="rounded-md3-full"
                disabled={cancelDialog.saving}
              >
                Keep RFQ
              </Button>
              <Button
                type="button"
                onClick={confirmCancel}
                disabled={cancelDialog.saving || (cancelDialog.remarks || "").trim().length < 3}
                className="rounded-md3-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="rfq-cancel-confirm-btn"
              >
                {cancelDialog.saving ? "Cancelling..." : "Cancel RFQ"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
